import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { WbAspectRatio, WbButton, WbIcon, WbSelect } from '@workbench/react';

import VideoJS from '../Common/VideoJS/VideoJS';
import './LandingPage.css';
import landingStageImage from '../../Assets/Images/landing-stage.jpg';
import retailDataTeaser from '../../Assets/Images/retail-data-teaser.png';

const RETAIL_DATA_URL = 'https://connectivity.mercedes-benz.com/products/retail-data';
const DEVELOPERS_URL = 'https://developer.mercedes-benz.com/products';
const PRODUCT_URLS = {
	carConfigurator: 'https://connectivity.mercedes-benz.com/products/car-configurator',
	vehicleImages: 'https://connectivity.mercedes-benz.com/products/vehicle-images',
	vehicleSpecification: 'https://connectivity.mercedes-benz.com/products/vehicle-specification',
};

function LandingPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [selectedProduct, setSelectedProduct] = useState('');

	const products = [
		{ value: 'car-configurator', label: t('landing.products.carConfigurator'), url: PRODUCT_URLS.carConfigurator },
		{ value: 'vehicle-images', label: t('landing.products.vehicleImages'), url: PRODUCT_URLS.vehicleImages },
		{ value: 'vehicle-specification', label: t('landing.products.vehicleSpecification'), url: PRODUCT_URLS.vehicleSpecification },
	];

	const handleProductChange = (event) => {
		const product = products.find(({ value }) => value === event.target.value);

		if (product?.url) {
			window.open(product.url, '_blank', 'noopener,noreferrer');
			setSelectedProduct('');
		}
	};

	const openExternalUrl = (url) => window.open(url, '_blank', 'noopener,noreferrer');

	const cards = [
		{
			iconName: 'bds/media-play/16',
			tag: t('landing.demo.tag'),
			title: t('landing.demo.title'),
			description: t('landing.demo.description'),
			bullets: [
				t('landing.demo.bullets.vin'),
				t('landing.demo.bullets.page'),
			],
			action: t('landing.demo.action'),
			onClick: () => window.open(`/demo`, '_blank', 'noopener,noreferrer'),
		},
		{
			iconName: 'bds/download/16',
			tag: t('landing.products.tag'),
			title: t('landing.products.title'),
			description: t('landing.products.description'),
			bullets: [
				t('landing.products.images'),
				t('landing.products.equipment'),
				t('landing.products.specifications'),
				t('landing.products.models'),
			],
			action: t('landing.products.action'),
			onClick: () => navigate('/images'),
		},
	];

	const videoSources = [{
		src: `assets/retailer-dashboard-${i18n.language === 'de' ? 'de' : 'en'}.mp4`,
		type: 'video/mp4',
	}];

	return (
		<div className="landing-page">
			<section className="landing-stage" aria-labelledby="landing-stage-title">
				<div className="landing-stage-content landing-container">
					<h1 id="landing-stage-title">{t('landing.stage.title')}</h1>
					<p>{t('landing.stage.description')}</p>
				</div>
				<WbAspectRatio className="landing-stage-media" ratio="12x5">
					<img className="landing-stage-image" src={landingStageImage} alt="" />
					<div className="landing-stage-overlay" />
				</WbAspectRatio>
			</section>

			<section className="landing-section retail-data-section landing-container" aria-labelledby="retail-data-title">
				<div className="section-kicker">{t('landing.retailData.kicker')}</div>
				<div className="retail-data-grid">
					<div className="retail-data-copy">
						<h2 id="retail-data-title">{t('landing.retailData.title')}</h2>
						<p>{t('landing.retailData.description')}</p>
						<div className="retail-data-actions">
							<label htmlFor="landing-product-select" className="visually-hidden">
								{t('landing.retailData.selectLabel')}
							</label>
							<WbSelect compact>
								<select id="landing-product-select" aria-label={t('landing.retailData.selectLabel')} value={selectedProduct} onChange={handleProductChange}>
									<option value="">{t('landing.retailData.selectPlaceholder')}</option>
									{products.map((product) => (
										<option key={product.value} value={product.value}>{product.label}</option>
									))}
								</select>
							</WbSelect>
							<span className="retail-data-or">{t('landing.retailData.or')}</span>
							<WbButton size="l" variant="primary" onClick={() => openExternalUrl(RETAIL_DATA_URL)}>
								{t('landing.retailData.action')}
							</WbButton>
						</div>
					</div>
					<img className="retail-data-image" src={retailDataTeaser} alt={t('landing.retailData.imageAlt')} />
				</div>
			</section>

			<section className="landing-section demo-section landing-container" aria-labelledby="demo-section-title">
				<div className="demo-copy">
					<h2 id="demo-section-title">{t('landing.demoSection.title')}</h2>
					<p>{t('landing.demoSection.description')}</p>
				</div>
				<div className="demo-media">
					<div className="demo-video">
						<div className="demo-video-frame">
							<VideoJS sources={videoSources} />
						</div>
						<p className="video-caption">{t('landing.demoSection.videoCaption')}</p>
					</div>
				</div>
			</section>

			<section className="landing-section choices-section landing-container" aria-labelledby="choices-title">
				<h2 id="choices-title" className="visually-hidden">{t('landing.choices.title')}</h2>
				<div className="landing-cards">
					{cards.map((card) => (
						<article className="landing-card" key={card.title}>
							<span className="landing-card-tag">
								<span className="landing-card-tag-content">
									<WbIcon className="landing-card-tag-icon" data-testid="landing-card-tag-icon" name={card.iconName} />
									<span>{card.tag}</span>
								</span>
							</span>
							<h3>{card.title}</h3>
							<p>{card.description}</p>
							<ul>
								{card.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
							</ul>
							<WbButton size="m" className="landing-workbench-button" variant="primary" onClick={card.onClick}>
								{card.action}
							</WbButton>
						</article>
					))}
				</div>
			</section>

			<section className="landing-callout landing-container" aria-labelledby="product-key-title">
				<div className="landing-callout-accent" />
				<div className="landing-callout-content">
					<div>
						<h2 id="product-key-title">{t('landing.callout.title')}</h2>
						<p>{t('landing.callout.description')}</p>
					</div>
					<WbButton size="l" variant="primary" onClick={() => openExternalUrl(DEVELOPERS_URL)}>
						{t('landing.callout.action')}
					</WbButton>
				</div>
			</section>
		</div>
	);
}

export default LandingPage;